/**
 * Non-interactive sign-in roll-up.
 *
 * The feed arrives at (session, IP, hour) grain, so one idle laptop becomes ~25 rows a
 * day and 2,836 rows swamped a 31-day case. The fix is NOT to hide them -- 37 of 59 IPs
 * on that case exist ONLY in this feed, 3 of them classified malicious -- it is to
 * collapse them on the axes that carry signal and keep every constituent reachable.
 *
 * Measured on IR-20260818-Controller-001: 2,836 rows -> 442, with all 19,192
 * authentications, 59 IPs, 20 devices, 40 sessions and 8 error-code sets preserved.
 *
 * NOTE (v1.155.0): the hourly ROLL-UP this file originally provided was superseded within
 * a day by utils/accessPeriods.js, which produces fewer rows (60 vs 90 on the reference
 * case) from ONE transform and does not split at midnight -- the day boundary was cutting
 * one continuous stretch of attacker activity into five rows. The roll-up functions were
 * REMOVED rather than left beside their replacement. What remains here is the per-IP
 * aggregate + report IP-list merge (the operator's failure-only rule), the Application
 * column label, and the per-row description.
 *
 * The original key was (day, IP, device, outcome-class, errorCodes, session):
 *   - outcome-class + errorCodes: a failure is NEVER merged into a success. Averaging
 *     them would erase the 50173 revoke signal, which is the containment read.
 *   - session: it is the attribution axis (a non-interactive row ties back to its
 *     originating interactive sign-in only through the session id). Dropping it merged
 *     59 groups across sessions to save 82 rows -- not a trade worth making.
 *   - day: coarse enough to kill the noise, and the constituents ride along on
 *     _rolledIds / _hours so an expanded row still shows every hour.
 *
 * A session id is CORROBORATION, NEVER ATTRIBUTION -- a stolen token keeps the victim's
 * session id. Nothing here may assert whose activity a row represents.
 */

import { spanLabel } from './accessPeriods.js';

const num = (v) => Number(v) || 0;


/** Per-IP aggregate over the raw (un-rolled) feed. */
export function nonInteractiveIpAggregates(events) {
  const map = new Map();
  for (const evt of Array.isArray(events) ? events : []) {
    const ip = evt && evt.IPAddress;
    if (!ip) continue;
    let a = map.get(ip);
    if (!a) {
      a = {
        ipAddress: ip, authCount: 0, successCount: 0, failureCount: 0, hourRows: 0,
        devices: new Set(), sessions: new Set(), asns: new Set(), errorCodes: new Set(),
        firstUtc: '', lastUtc: '',
      };
      map.set(ip, a);
    }
    a.authCount += num(evt.EventCount);
    a.successCount += num(evt.SuccessCount);
    a.failureCount += num(evt.FailureCount);
    a.hourRows += 1;
    if (evt.DeviceName) a.devices.add(evt.DeviceName);
    if (evt.SessionId) a.sessions.add(evt.SessionId);
    // ASN 0 means unknown, never AS0.
    if (evt.ASN && String(evt.ASN) !== '0') a.asns.add(String(evt.ASN));
    if (evt.ErrorCodes) a.errorCodes.add(evt.ErrorCodes);
    const s = evt.EventTimeUTC || '';
    const e = evt.LastEventTimeUTC || evt.EventTimeUTC || '';
    if (s && (!a.firstUtc || s < a.firstUtc)) a.firstUtc = s;
    if (e && e > a.lastUtc) a.lastUtc = e;
  }
  return map;
}

/**
 * Merge non-interactive-only addresses into the report's IP list.
 *
 * OPERATOR RULE (2026-08-20, explicit): an ADDED address whose non-interactive record is
 * failures only is EXCLUDED -- "i don't use failed attempts from IPs as confirmation".
 * There is deliberately NO carve-out for a malicious or suspect classification; that
 * exception was proposed and declined.
 *
 * Scope, equally deliberate: this governs ADDITIONS ONLY. An address already in the list
 * from interactive activity is returned untouched whatever its failure record, and the
 * timeline still shows failure rows -- the rule is about naming an address in a report,
 * not about what the analyst sees on screen.
 */
export function mergeNonInteractiveIpList(ipAddresses, niEvents) {
  const base = Array.isArray(ipAddresses) ? ipAddresses : [];
  const existing = new Set(base.map((r) => r && r.IPAddress).filter(Boolean));
  const agg = nonInteractiveIpAggregates(niEvents);

  const added = [];
  for (const a of agg.values()) {
    if (existing.has(a.ipAddress)) continue;
    if (a.successCount === 0) continue;              // the operator rule
    const asn = [...a.asns][0] || '';
    added.push({
      IPAddress: a.ipAddress,
      // No enrichment row exists for these addresses. The ASN comes from the feed
      // itself, so it is reported as a bare AS number and never dressed up as a
      // provider name we do not have.
      Provider: asn ? `AS${asn}` : '',
      City: '', State: '', Country: '',
      RiskLevel: null,
      IsVPN: null, IsProxy: null, IsTOR: null, IsCompromised: null, IsAnonymous: null,
      DRMMMatched: null,
      EventCount: a.authCount,
      _nonInteractiveOnly: true,
      _niSuccessCount: a.successCount,
      _niFailureCount: a.failureCount,
      _niDevices: [...a.devices].sort(),
      _niSessionCount: a.sessions.size,
      _niFirstUtc: a.firstUtc,
      _niLastUtc: a.lastUtc,
    });
  }
  added.sort((x, y) => (y.EventCount - x.EventCount) || x.IPAddress.localeCompare(y.IPAddress));
  return base.concat(added);
}

/**
 * Label for the All Events "Application" column.
 *
 * That column is really application-or-operation: it falls back to `Operation` for
 * non-sign-in rows, and Operation on these rows is the literal string "TokenRefresh" --
 * so every non-interactive row printed the same word, which is what made the feed look
 * content-free at a glance even though the row carries IP, device, apps and outcome.
 * The apps are the useful thing to put there.
 */
export function nonInteractiveAppLabel(evt) {
  const e = evt || {};
  // 🔴 An access-period row is spread from its FIRST constituent hour, so `Apps` on it
  // names only that hour's applications. Reading it on a multi-day period silently
  // under-reports -- one 60-hour period showed a single app when its constituents
  // spanned five. `_periodApps` is the period's own distinct set (accessPeriods.js).
  const period = Array.isArray(e._periodApps) ? e._periodApps : null;
  if (period && period.length) {
    const names = period.map((a) => a && a.name).filter(Boolean);
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(', ');
    if (names.length > 2) return `${names[0]} +${names.length - 1} more`;
  }
  const list = String(e.Apps || '').split(',').map((a) => a.trim()).filter(Boolean);
  const total = Math.max(num(e.AppCount), list.length);
  if (list.length === 0) return total ? `${total} app${total === 1 ? '' : 's'}` : 'Token refresh';
  if (total <= 2) return list.slice(0, 2).join(', ');
  return `${list[0]} +${total - 1} more`;
}

/**
 * The one-line description of a non-interactive row, rolled or not.
 *
 * ONE helper called by BOTH copies of generateDescription (eventUtils.js and
 * TimelineView.jsx). Those copies have diverged before, and a rolled row must not
 * describe itself one way on screen and another in a report.
 *
 * Deliberately NOT said: anything attributing the activity. The row carries a session
 * id, and a stolen token keeps the VICTIM's session id, so a shared session is
 * corroboration only. Attribution comes from the IP classification the analyst applies,
 * which colours the row through the normal mechanism.
 */
export function describeNonInteractive(event) {
  const e = event || {};
  const n = num(e.EventCount);
  const okCount = num(e.SuccessCount);
  const bad = num(e.FailureCount);
  // A DURATION, not clock times. This used to read "between 19:20 and 19:50 UTC" beside a
  // timestamp column rendered in the analyst's chosen zone, so the same row showed both
  // "3:20 PM" and "19:20" for one moment -- in a client-facing report. See spanLabel.
  const span = (e.LastEventTimeUTC && e.LastEventTimeUTC !== e.EventTimeUTC)
    ? spanLabel(e.EventTimeUTC, e.LastEventTimeUTC)
    : '';
  const noun = n === 1 ? 'authentication' : 'authentications';
  let outcome;
  if (okCount && bad) outcome = ` — ${okCount} succeeded, ${bad} failed`;
  else if (okCount) outcome = okCount === n ? ' — all succeeded' : ` — ${okCount} succeeded`;
  else if (bad) outcome = bad === n ? ' — all failed' : ` — ${bad} failed`;
  else outcome = '';
  const codes = e.ErrorCodes ? ` [${e.ErrorCodes}]` : '';
  // AppCount is the distinct total; Apps is capped, so disclose the overflow rather than
  // letting a truncated list read as complete.
  const appList = e.Apps ? String(e.Apps).split(',').filter(Boolean) : [];
  const total = num(e.AppCount) || appList.length;
  let apps = '';
  if (appList.length) {
    const shown = appList.slice(0, 3).join(', ');
    const more = total > 3 ? ` +${total - 3} more` : '';
    apps = ` via ${shown}${more}`;
  }
  const device = e.DeviceName ? ` from ${e.DeviceName}` : '';
  // Say the row is a roll-up. Without this, "511 authentications between 00:13 and 23:59"
  // reads as one continuous burst rather than 23 separate hours of ordinary background
  // activity -- and the analyst cannot tell that expanding the row will show more.
  const rolled = e._rolled && num(e._hourCount) > 1
    ? ` (${e._hourCount} hourly periods combined)`
    : '';
  return `${n} non-interactive ${noun}${span}${outcome}${codes}${apps}${device}${rolled}`;
}

/** How many addresses the operator rule withheld, so the report can disclose it rather
 *  than silently shortening the list. */
export function nonInteractiveExcludedIpCount(ipAddresses, niEvents) {
  const existing = new Set((Array.isArray(ipAddresses) ? ipAddresses : [])
    .map((r) => r && r.IPAddress).filter(Boolean));
  let n = 0;
  for (const a of nonInteractiveIpAggregates(niEvents).values()) {
    if (!existing.has(a.ipAddress) && a.successCount === 0) n += 1;
  }
  return n;
}
